// 「换脸配置」+ 定价 区块 — 嵌入套系编辑页 (Detail.tsx)。
//
// 控件绑定到套系 config.faceswap 子对象 (snake_case 字段名是前后端契约)。
// 与 price_cents 定价并列；选高算力引擎且定价偏低时给软提示 (不拦截保存)。
// openspec change: configurable-faceswap-fidelity, specs/faceswap-admin.

import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  InputNumber,
  Select,
  Slider,
  Space,
  Typography,
  Alert,
  message,
} from 'antd';
import {
  CODEFORMER_MODEL,
  COST_HINT_ENGINES,
  ENGINE_MIN_PRICE_CENTS,
  ENGINE_OPTIONS,
  FACE_MODEL_OPTIONS,
  RESTORE_MODEL_OPTIONS,
  type FaceSwapConfig,
  type FaceSwapEngine,
  type FaceSwapFaceModel,
  type FaceSwapRestoreModel,
} from '@/api/faceswap';
import { updatePackage, type ContentPackage } from '@/api/content';

const { Text } = Typography;

/** Render an option as 名称 + 灰色说明小字 (用于下拉里每项的解析)。 */
function optionWithDesc(opt: { label?: React.ReactNode; desc?: string }) {
  return (
    <div style={{ padding: '2px 0' }}>
      <div>{opt.label}</div>
      {opt.desc && (
        <div style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'normal', lineHeight: 1.4 }}>
          {opt.desc}
        </div>
      )}
    </div>
  );
}

interface Props {
  pkg: ContentPackage;
  onSaved: () => void;
}

/** Pull the existing faceswap sub-object out of a package config (may be absent). */
function readFaceSwap(config: Record<string, unknown> | undefined): FaceSwapConfig {
  const fs = (config?.faceswap ?? {}) as Record<string, unknown>;
  return {
    engine: fs.engine as FaceSwapEngine | undefined,
    restore_model: fs.restore_model as FaceSwapRestoreModel | undefined,
    restore_visibility:
      typeof fs.restore_visibility === 'number' ? fs.restore_visibility : undefined,
    codeformer_weight:
      typeof fs.codeformer_weight === 'number' ? fs.codeformer_weight : undefined,
    face_boost: typeof fs.face_boost === 'boolean' ? fs.face_boost : undefined,
    face_model: fs.face_model as FaceSwapFaceModel | undefined,
  };
}

export function FaceSwapConfigCard({ pkg, onSaved }: Props) {
  const initialFs = useMemo(() => readFaceSwap(pkg.config), [pkg.config]);

  const [engine, setEngine] = useState<FaceSwapEngine | undefined>(initialFs.engine);
  const [restoreModel, setRestoreModel] = useState<FaceSwapRestoreModel | undefined>(
    initialFs.restore_model,
  );
  const [restoreVisibility, setRestoreVisibility] = useState<number | undefined>(
    initialFs.restore_visibility,
  );
  const [codeformerWeight, setCodeformerWeight] = useState<number | undefined>(
    initialFs.codeformer_weight,
  );
  const [faceBoost, setFaceBoost] = useState<boolean>(initialFs.face_boost ?? false);
  const [faceModel, setFaceModel] = useState<FaceSwapFaceModel | undefined>(
    initialFs.face_model,
  );
  const [priceCents, setPriceCents] = useState<number | undefined>(pkg.price_cents);
  const [saving, setSaving] = useState(false);

  // codeformer_weight only meaningful for the codeformer restore model.
  const codeformerEnabled = restoreModel === CODEFORMER_MODEL;

  // Soft cost hint: high-compute engine + price below suggested floor.
  const costHint = useMemo(() => {
    if (!engine || !COST_HINT_ENGINES.includes(engine)) return null;
    const floor = ENGINE_MIN_PRICE_CENTS[engine];
    if (priceCents !== undefined && priceCents >= floor) return null;
    return `该引擎算力成本高，建议定价 ≥ ¥${(floor / 100).toFixed(1)}`;
  }, [engine, priceCents]);

  const onSave = async () => {
    setSaving(true);
    try {
      // Merge into existing config — preserve template_swap and any other keys.
      const baseConfig: Record<string, unknown> = { ...(pkg.config ?? {}) };

      // Only emit fields the operator actually set; undefined ⇒ omit so the
      // backend falls back to its platform default (field-level resolution).
      const faceswap: FaceSwapConfig = {};
      if (engine !== undefined) faceswap.engine = engine;
      if (restoreModel !== undefined) faceswap.restore_model = restoreModel;
      if (restoreVisibility !== undefined)
        faceswap.restore_visibility = restoreVisibility;
      if (codeformerEnabled && codeformerWeight !== undefined)
        faceswap.codeformer_weight = codeformerWeight;
      faceswap.face_boost = faceBoost;
      if (faceModel !== undefined) faceswap.face_model = faceModel;

      baseConfig.faceswap = faceswap;

      await updatePackage(pkg.id, {
        price_cents: priceCents,
        config: baseConfig,
      });
      message.success('换脸配置已保存');
      onSaved();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 底版换脸套系 (template_swap): jimeng(即梦人物参考) 不读底版 → 会被路由判为误配而失败,
  // 产品上也错(出的不是该套系场景)。故对底版套系隐藏 jimeng, 要"更像"用 pulid_reactor。
  const isTemplateSwap = ((pkg.config as Record<string, unknown> | undefined)?.template_swap === true);

  // 一键预设: 运营不用懂参数, 选档位自动填 engine/faceboost/restore。
  const ALL_PRESETS: { id: string; label: string; engine: FaceSwapEngine; faceBoost: boolean; visibility: number }[] = [
    { id: 'budget', label: '省钱 (基础换脸)', engine: 'reactor', faceBoost: false, visibility: 0.7 },
    { id: 'standard', label: '标准 (推荐·开FaceBoost)', engine: 'reactor', faceBoost: true, visibility: 0.7 },
    { id: 'multi', label: '多图更像 (传2-4张·快)', engine: 'reactor_multi', faceBoost: true, visibility: 0.7 },
    { id: 'hifi', label: '高保真 (PuLID重绘·慢)', engine: 'pulid_reactor', faceBoost: true, visibility: 0.7 },
    { id: 'asian', label: '亚洲脸 (即梦人物参考)', engine: 'jimeng', faceBoost: true, visibility: 0.7 },
  ];
  // 底版套系过滤掉 jimeng 预设
  const PRESETS = ALL_PRESETS.filter((p) => !(isTemplateSwap && p.engine === 'jimeng'));
  // 引擎下拉: 底版套系去掉 jimeng 选项
  const engineOptions = isTemplateSwap
    ? ENGINE_OPTIONS.filter((o) => o.value !== 'jimeng')
    : ENGINE_OPTIONS;
  // 当前选中态匹配哪个预设 (按 engine + faceBoost; visibility 各预设一致)。
  const activePresetId = useMemo(() => {
    const m = PRESETS.find((p) => p.engine === engine && p.faceBoost === faceBoost);
    return m?.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, faceBoost]);

  const applyPreset = (p: (typeof PRESETS)[number]) => {
    setEngine(p.engine);
    setFaceBoost(p.faceBoost);
    setRestoreVisibility(p.visibility);
    setRestoreModel(undefined); // 用平台默认 restore 模型 (GFPGAN)
    message.info(`已套用「${p.label.split(' ')[0]}」预设，记得点保存`);
  };

  return (
    <>
      <Divider orientation="left">换脸配置 + 定价</Divider>
      {isTemplateSwap && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="本套系是「底版换脸」(template_swap)：即梦人物参考(jimeng) 不读底版、会失败，已隐藏。要更像请用「高保真 (PuLID)」——它在底版上重绘保 ID。"
        />
      )}
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ marginRight: 8 }}>一键预设：</Text>
        <Space wrap>
          {PRESETS.map((p) => (
            <Button
              key={p.id}
              size="small"
              type={activePresetId === p.id ? 'primary' : 'default'}
              ghost={activePresetId === p.id}
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </Button>
          ))}
        </Space>
      </div>
      <Form layout="vertical" style={{ maxWidth: 520 }}>
        <Form.Item
          label="引擎 (engine)"
          extra="换脸引擎，决定保真度与算力成本。reactor=基础换脸(快/省) · reactor_multi=多图换脸(更像) · pulid_reactor=PuLID 重绘(最像/慢/贵) · jimeng=即梦人物参考。留空=用平台默认。"
        >
          <Select<FaceSwapEngine>
            placeholder="平台默认"
            allowClear
            value={engine}
            options={engineOptions}
            optionRender={(o) => optionWithDesc(o.data)}
            onChange={(v) => setEngine(v)}
          />
        </Form.Item>

        <Form.Item
          label="修复模型 (restore_model)"
          extra="换脸后的人脸修复模型。codeformer 通常比 GFPGAN 更自然、塑料感更轻；none=不修复(最保留原貌但可能粗糙)。留空=用平台默认。"
        >
          <Select<FaceSwapRestoreModel>
            placeholder="平台默认"
            allowClear
            value={restoreModel}
            options={RESTORE_MODEL_OPTIONS}
            optionRender={(o) => optionWithDesc(o.data)}
            onChange={(v) => setRestoreModel(v)}
          />
        </Form.Item>

        <Form.Item
          label="人脸模型 (face_model)"
          extra="single=单张人脸换脸(默认) · multi=多图合成更稳的身份(需上传 2-4 张脸，更像本人)。留空=用平台默认。"
        >
          <Select<FaceSwapFaceModel>
            placeholder="平台默认"
            allowClear
            value={faceModel}
            options={FACE_MODEL_OPTIONS}
            optionRender={(o) => optionWithDesc(o.data)}
            onChange={(v) => setFaceModel(v)}
          />
        </Form.Item>

        <Form.Item
          label={`修复可见度 (restore_visibility): ${
            restoreVisibility ?? '默认 (平台值 0.7)'
          }`}
          extra="修复强度。越靠左=保留换脸原貌、更自然但可能粗糙；越靠右=修复越强、更光滑但可能塑料/失真。推荐 0.7。留在最左不动=用平台默认。"
        >
          <Slider
            min={0}
            max={1}
            step={0.05}
            value={restoreVisibility ?? 0}
            onChange={(v) => setRestoreVisibility(v)}
            marks={{
              0: { label: '0 · 保真自然', style: { fontSize: 11 } },
              0.7: { label: '0.7 推荐', style: { fontSize: 11, color: '#52c41a' } },
              1: { label: '1 · 强修复光滑', style: { fontSize: 11 } },
            }}
          />
        </Form.Item>

        <Form.Item
          label={`CodeFormer 权重 (codeformer_weight): ${
            codeformerEnabled ? codeformerWeight ?? '默认 (平台值 0.7)' : '仅 CodeFormer 修复模型可用'
          }`}
          extra="仅当修复模型选 codeformer 时生效。越靠左=偏画质(更光滑、可能丢细节)；越靠右=偏保真(更留本人细节)。推荐 0.7。"
        >
          <Slider
            min={0}
            max={1}
            step={0.05}
            disabled={!codeformerEnabled}
            value={codeformerWeight ?? 0}
            onChange={(v) => setCodeformerWeight(v)}
            marks={
              codeformerEnabled
                ? {
                    0: { label: '0 · 偏画质', style: { fontSize: 11 } },
                    0.7: { label: '0.7 推荐', style: { fontSize: 11, color: '#52c41a' } },
                    1: { label: '1 · 偏保真', style: { fontSize: 11 } },
                  }
                : undefined
            }
          />
        </Form.Item>

        <Form.Item extra="开启 ReActorFaceBoost，对换脸区域做超分增强，五官更清晰。略增耗时。">
          <Checkbox
            checked={faceBoost}
            onChange={(e) => setFaceBoost(e.target.checked)}
          >
            face_boost（人脸增强）
          </Checkbox>
        </Form.Item>

        <Form.Item label="售价 price_cents（分）">
          <InputNumber
            min={0}
            style={{ width: 200 }}
            value={priceCents}
            onChange={(v) => setPriceCents(typeof v === 'number' ? v : undefined)}
            addonAfter={
              priceCents !== undefined ? `¥${(priceCents / 100).toFixed(2)}` : '¥-'
            }
          />
        </Form.Item>

        {costHint && (
          <Form.Item>
            <Alert type="warning" showIcon message={costHint} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              软提示，不影响保存。
            </Text>
          </Form.Item>
        )}

        <Space>
          <Button type="primary" loading={saving} onClick={onSave}>
            保存换脸配置
          </Button>
        </Space>
      </Form>
    </>
  );
}
